/**
 * Deterministic keyboard input (Master Spec §16, §17): exposes a discrete
 * -1/0/1 move direction, an edge-triggered "scan/inspect" action, an
 * edge-triggered "choose left/right" (Binary Search), and — as of Phase
 * 08 — a single edge-triggered "advance/resolve" sort decision shared by
 * Bubble Sort, Selection Sort, and Insertion Sort (each SortController
 * translates it back into its own engine's own "swap"/"keep",
 * "select"/"skip", or "shift"/"insert" vocabulary — see SortController.ts's
 * doc comment on `SortDecision`) — never raw event timing, so gameplay
 * decisions downstream never depend on how fast or how long a key was
 * held. The same physical Left/Right keys drive both continuous movement
 * and the edge-triggered direction choice; GameRuntime decides which
 * meaning applies (movement always applies, the choice is only consulted
 * during Binary Search's "awaiting choice" beat — see Master Spec §23).
 * Up/Down are reserved separately for the sort decision so it never
 * collides with movement.
 *
 * Phase 08 note: only one sorting algorithm is ever loaded at a time
 * (Master Spec §13), and now that GameRuntime dispatches every sorting
 * algorithm through the same `SortController` interface, there is no
 * longer a need for three separate per-algorithm queues
 * (`queuedSortDecision`/`queuedSelectionDecision`/`queuedInsertionDecision`,
 * Phase 05/06/07) — a single `queuedSortDecision: "advance" | "resolve"`
 * replaces all three, with Up/W meaning "resolve" (no further movement —
 * keep/skip/insert) and Down/S meaning "advance" (actively changes the
 * tracked element — swap/select/shift).
 */
const ACTION_KEYS = new Set(["Space", "KeyE"]);

export class GridInputController {
  private readonly pressed = new Set<string>();
  private actionQueued = false;
  private queuedDirectionChoice: "left" | "right" | null = null;
  private queuedSortDecision: "advance" | "resolve" | null = null;

  constructor() {
    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);
    window.addEventListener("blur", this.clear);
    window.addEventListener("algoquest-action", this.onAction);
  }

  getMoveDirection(): -1 | 0 | 1 {
    const left = this.pressed.has("ArrowLeft") || this.pressed.has("KeyA");
    const right = this.pressed.has("ArrowRight") || this.pressed.has("KeyD");
    if (left === right) return 0;
    return left ? -1 : 1;
  }

  /** True exactly once per physical press of a scan/interact key. */
  consumeActionPressed(): boolean {
    if (this.actionQueued) {
      this.actionQueued = false;
      return true;
    }
    return false;
  }

  /** "left"/"right" exactly once per physical press of that arrow/letter key, else null. */
  consumeDirectionChoice(): "left" | "right" | null {
    const choice = this.queuedDirectionChoice;
    this.queuedDirectionChoice = null;
    return choice;
  }

  /** "advance"/"resolve" exactly once per physical press of Down/Up (or S/W), else null. */
  consumeSortDecision(): "advance" | "resolve" | null {
    const decision = this.queuedSortDecision;
    this.queuedSortDecision = null;
    return decision;
  }

  dispose(): void {
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);
    window.removeEventListener("blur", this.clear);
    window.removeEventListener("algoquest-action", this.onAction);
    this.pressed.clear();
    this.actionQueued = false;
    this.queuedDirectionChoice = null;
    this.queuedSortDecision = null;
  }

  clear = (): void => {
    this.pressed.clear(); this.actionQueued = false;
    this.queuedDirectionChoice = null; this.queuedSortDecision = null;
  };
  private onAction = (event: Event): void => {
    const action = (event as CustomEvent<string>).detail;
    if (action === "scan") this.actionQueued = true;
    if (action === "left" || action === "right") this.queuedDirectionChoice = action;
    if (action === "advance" || action === "resolve") this.queuedSortDecision = action;
  };
  private onKeyDown = (event: KeyboardEvent): void => {
    if (event.target instanceof HTMLElement && event.target.closest("input,select,textarea")) return;
    if (event.code === "Space" && event.target instanceof HTMLElement && event.target.closest("button")) return;
    if (event.code.startsWith("Arrow")) event.preventDefault();
    if (ACTION_KEYS.has(event.code)) {
      event.preventDefault();
      if (!this.pressed.has(event.code)) {
        this.actionQueued = true;
      }
    }

    if (!this.pressed.has(event.code)) {
      if (event.code === "ArrowLeft" || event.code === "KeyA") {
        this.queuedDirectionChoice = "left";
      } else if (event.code === "ArrowRight" || event.code === "KeyD") {
        this.queuedDirectionChoice = "right";
      } else if (event.code === "ArrowUp" || event.code === "KeyW") {
        this.queuedSortDecision = "resolve";
      } else if (event.code === "ArrowDown" || event.code === "KeyS") {
        this.queuedSortDecision = "advance";
      }
    }

    this.pressed.add(event.code);
  };

  private onKeyUp = (event: KeyboardEvent): void => {
    this.pressed.delete(event.code);
  };
}
